import React from 'react';

/**
 * Reusable 3D Lantern (SVG) – reference-accurate shape
 *
 * Props
 * - size: "sm" | "md" | "lg" (default: "md")
 * - state: "lit" | "unlit" (default: "unlit")
 * - color: "yellow" | "red" | "blue" (default: "yellow")
 * - className: optional wrapper class
 */

export type LanternSize = 'sm' | 'md' | 'lg';

export type LanternState = 'lit' | 'unlit';

export type LanternColor = 'yellow' | 'red' | 'blue';

export interface LanternProps {
  size?: LanternSize;
  state?: LanternState;
  color?: LanternColor;
  className?: string;
  width?: number | string;
  height?: number | string;
  onClick?: () => void;
}

const SIZE_PX: Record<LanternSize, { w: number; h: number }> = {
  // sm: { w: 90, h: 116 },
  // md: { w: 130, h: 174 },
  // lg: { w: 180, h: 240 },
  lg: { w: 74.02, h: 84 },
  md: { w: 64.87, h: 74.02 },
  sm: { w: 43.06, h: 48.87 },
};

/**
 * Palettes & per-state rendering parameters from your SVG references.
 * - top, bottom: gradient stops
 * - stroke: stroke color
 * - strokeOpacity: optional opacity from reference (default 1)
 * - fillOpacity: body fill opacity from reference (default 1)
 * - offset: gradient 2nd stop offset (default 1)
 */
const SCHEMES: Record<
  LanternColor,
  Record<
    LanternState,
    {
      top: string;
      bottom: string;
      stroke: string;
      strokeOpacity?: number;
      fillOpacity?: number;
      offset?: number; // 2nd stop offset
    }
  >
> = {
  yellow: {
    // From 55x75 lit SVG (#FF9C24 -> #E4EBCB, stroke #FFCE0B)
    lit: {
      top: '#FF9C24',
      bottom: '#E4EBCB',
      stroke: '#FFCE0B',
      fillOpacity: 1,
      strokeOpacity: 1,
      offset: 1,
    },
    // From 55x75 unlit SVG (#F4820D -> #FFFA95, stroke #F27902, fillOpacity .5)
    unlit: {
      top: '#F4820D',
      bottom: '#FFFA95',
      stroke: '#F27902',
      fillOpacity: 0.5,
      strokeOpacity: 1,
      offset: 1,
    },
  },
  red: {
    // From 65x85 lit SVG (#D04A79 -> #FFFA95 @ 0.673, stroke #FFA5F3 @ .8)
    lit: {
      top: '#D04A79',
      bottom: '#FFFA95',
      stroke: '#FFA5F3',
      strokeOpacity: 0.8,
      fillOpacity: 1,
      offset: 0.673077,
    },
    // From 65x85 unlit SVG (#BF72A9 -> #FFFA95 @ 0.673, stroke #FF4955 @ .8, fillOpacity .5)
    unlit: {
      top: '#BF72A9',
      bottom: '#FFFA95',
      stroke: '#FF4955',
      strokeOpacity: 0.8,
      fillOpacity: 0.5,
      offset: 0.673077,
    },
  },
  blue: {
    // From 65x84 lit SVG (#9188C6 -> #FFFA95 @ 0.673, stroke #E0D7FF, fillOpacity .95)
    lit: {
      top: '#9188C6',
      bottom: '#FFFA95',
      stroke: '#E0D7FF',
      fillOpacity: 0.95,
      strokeOpacity: 1,
      offset: 0.673077,
    },
    // From 65x84 unlit SVG (#9188C6 -> #FFFA95 @ 0.673, stroke #9D8FD0, fillOpacity .4)
    unlit: {
      top: '#9188C6',
      bottom: '#FFFA95',
      stroke: '#9D8FD0',
      fillOpacity: 0.4,
      strokeOpacity: 1,
      offset: 0.673077,
    },
  },
};

export default function Lantern({
  size = 'md',
  state = 'unlit',
  color = 'yellow',
  className,
  width,
  height,
  onClick,
}: LanternProps) {
  const id = Math.random() * 10000;
  const dims = SIZE_PX[size];
  const scheme = SCHEMES[color][state];

  const w = width ?? dims.w;
  const h = height ?? dims.h;

  // Canonical shape/viewBox from the blue shape ref (65 x 84)
  const vbW = 65;
  const vbH = 84;

  const gradId = `lantern-grad-${id}`;
  const bgRadialId = `lantern-shadow-${id}`;

  // Calculate gradientTransform based on lantern size
  let gradientTransform;
  if (size === 'lg') {
    gradientTransform = 'translate(32 45) scale(100)'; // was 85
  }
  else if (size === 'md') {
    gradientTransform = 'translate(28 40) scale(100)'; // was 70
  }
  else {
    gradientTransform = 'translate(30 40) scale(100)'; // was 45
  }

  // Calculate rect position and size for shadow based on lantern size
  let shadowRect = { x: -35, y: -25, width: 100, height: 100 };
  if (size === 'sm') {
    shadowRect = { x: -60, y: -55, width: 170, height: 170 };
  }
  else if (size === 'md') {
    shadowRect = { x: -55, y: -45, width: 160, height: 160 };
  }
  else if (size === 'lg') {
    shadowRect = { x: -70, y: -70, width: 220, height: 220 };
  }

  function renderRadialStops(lanternColor: LanternColor) {
    if (lanternColor === 'yellow') {
      return (
        <>
          <stop stopColor='#FF8418' />
          <stop offset='0.239572' stopColor='#F3C46E' />
          <stop offset='0.424395' stopColor='#D3914B' stopOpacity='0.66' />
          <stop offset='0.566612' stopColor='#6C530A' stopOpacity='0.43' />
          <stop offset='0.63' stopColor='#3D2F05' stopOpacity='0.24' />
          <stop offset='0.78' stopColor='#1C1502' stopOpacity='0.11' />
          <stop offset='0.91' stopColor='#070500' stopOpacity='0.03' />
          <stop offset='1' stopOpacity='0' />
        </>
      );
    }
    if (lanternColor === 'red') {
      return (
        <>
          <stop stopColor='#FF4218' />
          <stop offset='0.196326' stopColor='#F86BCB' stopOpacity='0.94' />
          <stop offset='0.371029' stopColor='#E55B82' stopOpacity='0.66' />
          <stop offset='0.499021' stopColor='#6C530A' stopOpacity='0.43' />
          <stop offset='0.63' stopColor='#3D2F05' stopOpacity='0.24' />
          <stop offset='0.78' stopColor='#1C1502' stopOpacity='0.11' />
          <stop offset='0.91' stopColor='#070500' stopOpacity='0.03' />
          <stop offset='1' stopOpacity='0' />

        </>
      );
    }
    // blue
    return (
      <>
        <stop stopColor='#A5CBFF' />
        <stop offset='0.0802853' stopColor='#A5CBFF' stopOpacity='0.94' />
        <stop offset='0.120624' stopColor='#A4C5F7' stopOpacity='0.914364' />
        <stop offset='0.362939' stopColor='#9F88A9' stopOpacity='0.66' />
        <stop offset='0.543269' stopColor='#6C530A' stopOpacity='0.43' />
        <stop offset='0.63' stopColor='#3D2F05' stopOpacity='0.24' />
        <stop offset='0.78' stopColor='#1C1502' stopOpacity='0.11' />
        <stop offset='0.91' stopColor='#070500' stopOpacity='0.03' />
        <stop offset='1' stopOpacity='0' />
      </>
    );
  }

  return (
    <svg
      className={className}
      width={w}
      height={h}
      viewBox={`0 0 ${vbW} ${vbH}`}
      role='img'
      aria-label={`Paper lantern ${color} ${state}`}
      onClick={onClick}
      style={{
        cursor: 'pointer',
        overflow: 'visible',
        zIndex: 1,
      }}
    >
      <defs>
        {/* Body gradient */}
        <linearGradient
          id={gradId}
          x1='43.5959'
          y1='1.85766'
          x2='29.5851'
          y2='81.8953'
          gradientUnits='userSpaceOnUse'
        >
          <stop stopColor={scheme.top} />
          <stop offset={scheme.offset ?? 1} stopColor={scheme.bottom} />
        </linearGradient>

        {/* Background radial "shadow"/glow (from your three shadow SVGs).
            Placed/Scaled to sit behind our 65x84 viewBox. */}
        <radialGradient
          id={bgRadialId}
          cx='0'
          cy='0'
          r='1'
          gradientUnits='userSpaceOnUse'
          gradientTransform={gradientTransform}
        >
          {renderRadialStops(color)}
        </radialGradient>
      </defs>

      {/* Background shadow/halo (lit only) */}
      {state === 'lit' && (
        <rect
          x={shadowRect.x}
          y={shadowRect.y}
          width={shadowRect.width}
          height={shadowRect.height}
          fill={`url(#${bgRadialId})`}
          /* same as your refs */
          style={{ mixBlendMode: 'color-dodge' }}
          opacity={0.8}
        />
      )}

      {/* Body fill – reference path */}
      <path
        d='M0.883279 75.7182L4.26353 3.649L40.8265 1.08161L51.0839 3.95601L63.579 9.89808L61.7218 71.0044L60.4689 80.2125C60.4689 80.2125 57.8641 84.7707 30.4833 82.9961C3.10246 81.2214 0.883279 75.7182 0.883279 75.7182Z'
        fill={`url(#${gradId})`}
        fillOpacity={scheme.fillOpacity ?? 1}
      />

      {/* Structural strokes – reference lines */}
      <path
        d='M51.0839 3.95601L44.2917 74.3018M51.0839 3.95601L40.8265 1.08161L4.26353 3.649L0.883279 75.7182M51.0839 3.95601L63.579 9.89808L61.7218 71.0044L60.4689 80.2125M0.883279 75.7182C0.883279 75.7182 3.07784 71.0451 30.4587 72.8197C57.8395 74.5944 60.4689 80.2125 60.4689 80.2125M0.883279 75.7182C0.883279 75.7182 3.10246 81.2214 30.4833 82.9961C57.8641 84.7707 60.4689 80.2125 60.4689 80.2125'
        stroke={scheme.stroke}
        strokeOpacity={scheme.strokeOpacity ?? 1}
        strokeWidth={1.2}
        fill='none'
      />
    </svg>
  );
}
